import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fieldStatus"]

  initialize() {
    // Track when each status indicator was last updated (for minimum display time)
    this.statusTimestamps = new Map()
  }

  // Helper to generate data-test ID for status icons
  getTestId(questionId, fieldKey, status) {
    const base = fieldKey ? `question-${questionId}-${fieldKey}` : `question-${questionId}`
    return `${base}-answer-${status}`
  }

  // Helper to generate icon HTML with data-test attribute
  // Wrap in span because FontAwesome replaces <i> with <svg>, losing data-test
  getStatusIconHTML(iconClass, colorClass, questionId, fieldKey, status) {
    return `<span data-test="${this.getTestId(questionId, fieldKey, status)}"><i class="fas ${iconClass} ${colorClass}"></i></span>`
  }

  // Called by auto-save controller when saving starts
  showSaving(questionId, fieldKey = null) {
    const statusElement = this.getStatusElement(questionId, fieldKey)
    if (statusElement) {
      this.cancelPendingTimeout(statusElement)
      statusElement.innerHTML = this.getStatusIconHTML('fa-spinner fa-spin', 'text-primary', questionId, fieldKey, 'saving')
      statusElement.classList.remove('d-none')
      statusElement.dataset.status = 'saving'
      this.statusTimestamps.set(statusElement, Date.now())
    }
  }

  // Called by auto-save controller when save succeeds
  showSaved(questionId, fieldKey = null) {
    const statusElement = this.getStatusElement(questionId, fieldKey)
    if (statusElement) {
      this.cancelPendingTimeout(statusElement)
      statusElement.innerHTML = this.getStatusIconHTML('fa-check-circle', 'text-success', questionId, fieldKey, 'success')
      statusElement.classList.remove('d-none')
      statusElement.dataset.status = 'saved'

      const now = Date.now()
      this.statusTimestamps.set(statusElement, now)

      const minDisplayTime = 250
      const clearDelay = 2000

      const timeoutId = setTimeout(() => {
        const elapsed = Date.now() - this.statusTimestamps.get(statusElement)
        if (elapsed < minDisplayTime) {
          setTimeout(() => {
            this.clearStatus(statusElement)
          }, minDisplayTime - elapsed)
        } else {
          this.clearStatus(statusElement)
        }
      }, clearDelay)

      statusElement.dataset.clearTimeoutId = timeoutId
    }
  }

  // Helper to cancel any pending clear timeout
  cancelPendingTimeout(statusElement) {
    if (statusElement.dataset.clearTimeoutId) {
      clearTimeout(parseInt(statusElement.dataset.clearTimeoutId))
      delete statusElement.dataset.clearTimeoutId
    }
  }

  // Helper to clear status indicator
  clearStatus(statusElement) {
    statusElement.classList.add('d-none')
    statusElement.innerHTML = ''
    delete statusElement.dataset.status
    delete statusElement.dataset.clearTimeoutId
    this.statusTimestamps.delete(statusElement)
  }

  // Called by auto-save controller when save fails
  showError(questionId, fieldKey = null) {
    const statusElement = this.getStatusElement(questionId, fieldKey)
    if (statusElement) {
      this.cancelPendingTimeout(statusElement)
      statusElement.innerHTML = this.getStatusIconHTML('fa-exclamation-circle', 'text-danger', questionId, fieldKey, 'failed')
      statusElement.classList.remove('d-none')
      statusElement.dataset.status = 'error'
      this.statusTimestamps.set(statusElement, Date.now())

      const minDisplayTime = 250
      const clearDelay = 3000

      const timeoutId = setTimeout(() => {
        const elapsed = Date.now() - this.statusTimestamps.get(statusElement)
        if (elapsed < minDisplayTime) {
          setTimeout(() => {
            this.clearStatus(statusElement)
          }, minDisplayTime - elapsed)
        } else {
          this.clearStatus(statusElement)
        }
      }, clearDelay)

      statusElement.dataset.clearTimeoutId = timeoutId
    }
  }

  // Called when field validation fails (persists until valid)
  showValidationError(questionId, fieldKey = null) {
    const statusElement = this.getStatusElement(questionId, fieldKey)
    if (statusElement) {
      this.cancelPendingTimeout(statusElement)
      statusElement.innerHTML = this.getStatusIconHTML('fa-exclamation-circle', 'text-danger', questionId, fieldKey, 'failed')
      statusElement.classList.remove('d-none')
      statusElement.dataset.status = 'validation-error'
      this.statusTimestamps.set(statusElement, Date.now())
    }
  }

  // Called to clear validation error when field becomes valid
  clearValidationError(questionId, fieldKey = null) {
    const statusElement = this.getStatusElement(questionId, fieldKey)
    if (statusElement) {
      this.clearStatus(statusElement)
    }
  }

  // Get the status element for a specific question (and optional field key for composite fields)
  getStatusElement(questionId, fieldKey = null) {
    if (!questionId) return null

    return this.fieldStatusTargets.find(element => {
      const matchesQuestionId = element.dataset.questionId === questionId
      // If fieldKey is provided, match it; otherwise match elements without fieldKey
      const matchesFieldKey = fieldKey
        ? element.dataset.fieldKey === fieldKey
        : !element.dataset.fieldKey

      return matchesQuestionId && matchesFieldKey
    })
  }
}
